import Router from 'next/router';
import {
  Paper,
  InputBase,
  MenuList,
  MenuItem,
  Typography,
  TypographyProps,
  Tooltip,
} from '@mui/material';
import createStyles from '@mui/styles/createStyles';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { routeToFilter } from '../../../shared/functions';
import { Filter, FilterMap } from './Filter';

interface Props {
  /*
   * Filter type
   * */
  filter: Filter;
  /*
   * array max size should be 2, an InputBase component is inserted between the two strings.
   * */
  filterSelectedCallback?: (filterValue: number, queryName: string) => void;
  /*
   * array max size should be 2, an InputBase component is inserted between the two strings.
   * */
  inputTextList?: string[];
  /*
   * defines if the router state should be preserved on routing.
   * */
  keepRouterContext?: boolean;
}

const useStyles = makeStyles(() =>
  createStyles({
    input: {
      '& input[type=number]': {
        '-moz-appearance': 'textfield',
      },
      '& input[type=number]::-webkit-outer-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
      '& input[type=number]::-webkit-inner-spin-button': {
        '-webkit-appearance': 'none',
        margin: 0,
      },
    },
    paper: {
      maxWidth: 40,
      marginLeft: 5,
      marginRight: 5,
      paddingLeft: 3,
      paddingRight: 3,
    },
  }),
);

const CustomTypography = (props: TypographyProps) => (
  <Typography {...props} variant={'subtitle1'} />
);
const ITEM_PADDINGS = {
  paddingLeft: 12,
  paddingRight: 12,
};

export default function FilteringMenu(props: Props) {
  const classes = useStyles();

  const [customFilterValue, setCustomFilterValue] = useState<number>(null);

  useEffect(() => {
    if (props.inputTextList && props.inputTextList.length > 2) {
      throw new Error(
        `inputTextList prop max length should be 2 instead of ${props.inputTextList.length}`,
      );
    }
  }, [props.inputTextList]);

  const routeTo = (filterValue) => {
    routeToFilter(filterValue, Router, props.filter, props.keepRouterContext);
    if (props.filterSelectedCallback) {
      props.filterSelectedCallback(filterValue, props.filter.queryName);
    }
  };

  const onInputBaseClick = (event) => {
    event.stopPropagation();
  };

  const onChange = (event) => {
    const value = parseInt(event.currentTarget.value, 10);

    if ((value >= 0 && value <= 1000000) || !value) {
      setCustomFilterValue(value);
    }
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Enter') {
      event.preventDefault();

      routeTo(customFilterValue);
    }
  }

  return (
    <MenuList
      id="menu-list"
      onKeyDown={handleListKeyDown}
      onClick={(event) => event.stopPropagation()}
      sx={{
        maxHeight: 400,
        overflowY: 'auto',
        py: 0.5,
        minWidth: 180,
      }}
    >
      {props.filter.filterMaps.map((filterMap: FilterMap) =>
        filterMap.filterValue === 0 ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              ...ITEM_PADDINGS,
            }}
            onClick={(event) => event.stopPropagation()}
          >
            <CustomTypography
              style={{
                color: filterMap.color,
                fontWeight: filterMap.bold ? 'bold' : null,
              }}
            >
              {props.inputTextList[0]}
            </CustomTypography>
            <Paper component="form" className={classes.paper}>
              <InputBase
                value={customFilterValue}
                type="number"
                className={classes.input}
                onClick={onInputBaseClick}
                onChange={onChange}
              />
            </Paper>
            <CustomTypography
              style={{
                color: filterMap.color,
                fontWeight: filterMap.bold ? 'bold' : null,
              }}
            >
              {props.inputTextList[1]}
            </CustomTypography>
          </div>
        ) : (
          <Tooltip
            key={filterMap.filterTitle}
            title={filterMap.tooltip || ''}
            placement="right"
            arrow
          >
            <MenuItem
              style={{ ...ITEM_PADDINGS }}
              sx={{
                borderRadius: 1,
                mx: 0.5,
                '&:hover': { backgroundColor: 'rgba(92, 107, 192, 0.1)' },
              }}
              onClick={() => routeTo(filterMap.filterValue)}
            >
              <CustomTypography
                style={{
                  color: filterMap.color,
                  fontWeight: filterMap.bold ? 600 : 400,
                }}
              >
                {filterMap.filterTitle}
              </CustomTypography>
              {filterMap.icon && (
                <>
                  <div style={{ flexGrow: 1 }} />
                  <Icon
                    icon={filterMap?.icon}
                    style={{ marginLeft: 8, color: '#888888' }}
                    fontSize={25}
                  />
                </>
              )}
            </MenuItem>
          </Tooltip>
        ),
      )}
    </MenuList>
  );
}

FilteringMenu.defaultProps = {
  keepRouterContext: true,
};
